import { Brain } from "lucide-react";
import { brand } from "../../design/brand";

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-16">
      <h2 className="mb-6 text-xs font-semibold uppercase tracking-[0.15em] text-teal-400">{title}</h2>
      {children}
    </section>
  );
}

function Swatch({ hex, name, note }: { hex: string; name: string; note?: string }) {
  return (
    <div className="flex flex-col gap-2">
      <div
        className="h-16 w-full rounded-xl border border-white/10"
        style={{ background: hex }}
      />
      <div>
        <p className="text-sm font-medium text-white">{name}</p>
        <p className="font-mono text-xs text-slate-500">{hex}</p>
        {note && <p className="mt-0.5 text-xs text-slate-500">{note}</p>}
      </div>
    </div>
  );
}

function GradientSwatch({ from, to, name, note }: { from: string; to: string; name: string; note?: string }) {
  return (
    <div className="flex flex-col gap-2">
      <div
        className="h-16 w-full rounded-xl border border-white/10"
        style={{ background: `linear-gradient(135deg, ${from} 0%, ${to} 100%)` }}
      />
      <div>
        <p className="text-sm font-medium text-white">{name}</p>
        <p className="font-mono text-xs text-slate-500">{from} → {to}</p>
        {note && <p className="mt-0.5 text-xs text-slate-500">{note}</p>}
      </div>
    </div>
  );
}

function TypeSample({
  size,
  weight,
  label,
  text = "Aprendizado adaptativo",
}: {
  size: string;
  weight: number;
  label: string;
  text?: string;
}) {
  return (
    <div className="flex items-baseline gap-6 border-b border-white/[0.06] py-4 last:border-0">
      <div className="w-36 shrink-0 text-xs text-slate-500">{label}</div>
      <p className="min-w-0 text-white" style={{ fontSize: size, fontWeight: weight }}>
        {text}
      </p>
    </div>
  );
}

export default function BrandKit() {
  return (
    <div
      className="min-h-screen px-6 py-16 text-white"
      style={{ background: brand.color.bg.canvas, fontFamily: brand.typography.family.sans }}
    >
      <div className="mx-auto max-w-5xl">
        {/* Header */}
        <div className="mb-20 border-b border-white/[0.08] pb-12">
          <div className="mb-6 flex items-center gap-3">
            <div
              className="flex items-center justify-center rounded-xl"
              style={{
                width: 48,
                height: 48,
                background: brand.logo.iconBg,
                boxShadow: brand.logo.iconShadow,
              }}
            >
              <Brain className="h-6 w-6 text-white" />
            </div>
            <span className="text-2xl font-semibold tracking-tight">{brand.name}</span>
          </div>
          <h1 className="text-4xl font-semibold tracking-tight text-white">Brand Kit</h1>
          <p className="mt-3 max-w-xl text-base text-slate-400">{brand.tagline}</p>
          <p className="mt-1 text-sm text-slate-500">
            Este arquivo é o ponto de edição central. Altere{" "}
            <code className="rounded bg-white/5 px-1.5 py-0.5 font-mono text-xs text-teal-300">
              src/design/brand.ts
            </code>{" "}
            e este preview atualiza automaticamente.
          </p>
        </div>

        {/* Logo */}
        <Section title="Logo & Ícone">
          <div className="grid gap-8 sm:grid-cols-3">
            <div className="flex flex-col gap-3">
              <div className="flex h-32 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.03]">
                <div className="flex items-center gap-3">
                  <div
                    className="flex items-center justify-center rounded-xl"
                    style={{ width: 40, height: 40, background: brand.logo.iconBg }}
                  >
                    <Brain className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-lg font-semibold">NeuroMent</span>
                </div>
              </div>
              <p className="text-xs text-slate-500">Horizontal (fundo escuro)</p>
            </div>

            <div className="flex flex-col gap-3">
              <div className="flex h-32 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.03]">
                <div className="flex flex-col items-center gap-2">
                  <div
                    className="flex items-center justify-center rounded-2xl"
                    style={{ width: 56, height: 56, background: brand.logo.iconBg, boxShadow: brand.logo.iconShadow }}
                  >
                    <Brain className="h-7 w-7 text-white" />
                  </div>
                  <span className="text-sm font-semibold">NeuroMent</span>
                </div>
              </div>
              <p className="text-xs text-slate-500">Vertical (ícone + nome)</p>
            </div>

            <div className="flex flex-col gap-3">
              <div className="flex h-32 items-center justify-center rounded-2xl border border-white/10 bg-[#ffffff]">
                <div className="flex items-center gap-3">
                  <div
                    className="flex items-center justify-center rounded-xl"
                    style={{ width: 40, height: 40, background: brand.logo.iconBg }}
                  >
                    <Brain className="h-5 w-5 text-white" />
                  </div>
                  <span className="text-lg font-semibold text-[#050a14]">NeuroMent</span>
                </div>
              </div>
              <p className="text-xs text-slate-500">Fundo claro (usar com cautela)</p>
            </div>
          </div>
        </Section>

        {/* Cores */}
        <Section title="Cores — Fundos">
          <div className="grid gap-4 sm:grid-cols-4">
            <Swatch hex={brand.color.bg.canvas} name="Canvas" note="Fundo principal" />
            <Swatch hex={brand.color.bg.footer} name="Footer" note="Superfícies mais profundas" />
            <Swatch hex={brand.color.bg.surface} name="Surface" note="Cards e painéis" />
            <Swatch hex={brand.color.bg.glass} name="Glass" note="rgba(255,255,255,0.04)" />
          </div>
        </Section>

        <Section title="Cores — Primária (Gradiente de Marca)">
          <div className="grid gap-4 sm:grid-cols-3">
            <GradientSwatch
              from={brand.color.primary.from}
              to={brand.color.primary.to}
              name="Primary Gradient"
              note="Botões CTA, ícones, destaques"
            />
            <Swatch hex={brand.color.primary.from} name="Teal 400" note="Ponta clara do gradiente" />
            <Swatch hex={brand.color.primary.to} name="Cyan 600" note="Ponta escura do gradiente" />
          </div>
        </Section>

        <Section title="Cores — Texto">
          <div className="grid gap-4 sm:grid-cols-5">
            <Swatch hex={brand.color.text.primary} name="Primary" note="Títulos" />
            <Swatch hex={brand.color.text.secondary} name="Secondary" note="Corpo" />
            <Swatch hex={brand.color.text.muted} name="Muted" note="Subtítulos" />
            <Swatch hex={brand.color.text.hint} name="Hint" note="Metadados, datas" />
            <Swatch hex={brand.color.text.accent} name="Accent" note="Links, teal-300" />
          </div>
        </Section>

        <Section title="Cores — Semânticas">
          <div className="grid gap-4 sm:grid-cols-4">
            <Swatch hex={brand.color.semantic.success} name="Success" note="Confirmações" />
            <Swatch hex={brand.color.semantic.warning} name="Warning" note="Alertas" />
            <Swatch hex={brand.color.semantic.error} name="Error" note="Erros" />
            <Swatch hex={brand.color.semantic.info} name="Info" note="Informações" />
          </div>
        </Section>

        <Section title="Cores — Papéis (Personas)">
          <div className="grid gap-4 sm:grid-cols-5">
            {(Object.entries(brand.color.role) as [string, { from: string; to: string }][]).map(([role, c]) => (
              <GradientSwatch key={role} from={c.from} to={c.to} name={role.charAt(0).toUpperCase() + role.slice(1)} />
            ))}
          </div>
        </Section>

        {/* Tipografia */}
        <Section title="Tipografia">
          <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] px-6">
            <TypeSample size={brand.typography.size.hero} weight={brand.typography.weight.semibold} label="Hero / H1" text="NeuroMent" />
            <TypeSample size={brand.typography.size["4xl"]} weight={brand.typography.weight.semibold} label="Display / H2" />
            <TypeSample size={brand.typography.size["3xl"]} weight={brand.typography.weight.semibold} label="Heading / H3" />
            <TypeSample size={brand.typography.size["2xl"]} weight={brand.typography.weight.semibold} label="Subheading / H4" />
            <TypeSample size={brand.typography.size.xl} weight={brand.typography.weight.medium} label="Lead / Large" text="Personalização por perfil e comportamento" />
            <TypeSample size={brand.typography.size.base} weight={brand.typography.weight.normal} label="Body" text="O questionário gera um perfil canônico que alimenta decisões auditáveis." />
            <TypeSample size={brand.typography.size.sm} weight={brand.typography.weight.normal} label="Caption / Small" text="Cadastro sem cartão · LGPD alinhado ao uso escolar" />
            <TypeSample size={brand.typography.size.xs} weight={brand.typography.weight.medium} label="Label / Micro" text="ECOSSISTEMA · PLANOS · PERSONALIZAÇÃO" />
          </div>
        </Section>

        {/* Bordas e raios */}
        <Section title="Border Radius">
          <div className="flex flex-wrap gap-4">
            {(Object.entries(brand.radius) as [string, string][]).map(([name, value]) => (
              <div key={name} className="flex flex-col items-center gap-2">
                <div
                  className="h-14 w-14 border-2 border-teal-400/50 bg-teal-500/10"
                  style={{ borderRadius: value }}
                />
                <span className="text-xs text-slate-400">{name}</span>
                <span className="font-mono text-[10px] text-slate-600">{value}</span>
              </div>
            ))}
          </div>
        </Section>

        {/* Componentes */}
        <Section title="Componentes — Botões">
          <div className="flex flex-wrap gap-4">
            <button
              className="flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-semibold text-white shadow-lg transition-all hover:scale-[1.02]"
              style={{ background: brand.color.primary.gradient, boxShadow: brand.shadow.glow }}
            >
              <Brain className="h-4 w-4" />
              CTA Principal
            </button>

            <button
              className="rounded-full border px-6 py-2.5 text-sm font-medium text-slate-100 backdrop-blur transition-all hover:border-teal-400/40 hover:text-white"
              style={{ borderColor: brand.color.border.default, background: brand.color.bg.glass }}
            >
              Secundário
            </button>

            <button
              className="rounded-full border px-6 py-2.5 text-sm font-medium transition-all hover:bg-white/10"
              style={{ borderColor: brand.color.border.subtle, color: brand.color.text.secondary }}
            >
              Ghost
            </button>

            <button
              className="rounded-full border border-red-500/40 bg-red-500/10 px-6 py-2.5 text-sm font-medium text-red-300 transition-all hover:bg-red-500/20"
            >
              Destrutivo
            </button>
          </div>
        </Section>

        <Section title="Componentes — Cards">
          <div className="grid gap-4 sm:grid-cols-3">
            <div
              className="rounded-2xl border p-5"
              style={{ borderColor: brand.color.border.subtle, background: brand.color.bg.glass, backdropFilter: "blur(12px)" }}
            >
              <div
                className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl"
                style={{ background: brand.color.primary.gradient }}
              >
                <Brain className="h-5 w-5 text-white" />
              </div>
              <h3 className="font-semibold text-white">Card Padrão</h3>
              <p className="mt-1 text-sm text-slate-400">Glass com borda sutil. Usado em seções de features e destaques.</p>
            </div>

            <div
              className="rounded-2xl border-2 p-5"
              style={{ borderColor: brand.color.border.strong, background: "linear-gradient(135deg, rgba(19,78,74,0.6) 0%, rgba(15,23,42,0.9) 100%)", backdropFilter: "blur(16px)" }}
            >
              <div
                className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl"
                style={{ background: brand.color.primary.gradient }}
              >
                <Brain className="h-5 w-5 text-white" />
              </div>
              <h3 className="font-semibold text-white">Card Destaque</h3>
              <p className="mt-1 text-sm" style={{ color: "#99f6e4" }}>
                Borda teal forte + fundo teal-950. Plano recomendado, CTAs principais.
              </p>
            </div>

            <div
              className="rounded-2xl border p-5"
              style={{ borderColor: brand.color.border.default, background: brand.color.bg.surface }}
            >
              <div
                className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-white/10"
              >
                <Brain className="h-5 w-5 text-slate-300" />
              </div>
              <h3 className="font-semibold text-white">Card Neutro</h3>
              <p className="mt-1 text-sm text-slate-400">Fundo surface (#0d1525). Planos não-recomendados, itens secundários.</p>
            </div>
          </div>
        </Section>

        <Section title="Componentes — Badges">
          <div className="flex flex-wrap gap-3">
            {[
              { bg: "rgba(20,184,166,0.1)", border: "rgba(45,212,191,0.3)", text: "#99f6e4", label: "Teal (padrão)" },
              { bg: "rgba(6,182,212,0.1)", border: "rgba(34,211,238,0.3)", text: "#a5f3fc", label: "Cyan" },
              { bg: "rgba(139,92,246,0.1)", border: "rgba(167,139,250,0.3)", text: "#c4b5fd", label: "Violet" },
              { bg: "rgba(16,185,129,0.1)", border: "rgba(52,211,153,0.3)", text: "#6ee7b7", label: "Emerald" },
              { bg: "rgba(245,158,11,0.1)", border: "rgba(251,191,36,0.3)", text: "#fde68a", label: "Amber" },
              { bg: "rgba(239,68,68,0.1)", border: "rgba(252,165,165,0.3)", text: "#fca5a5", label: "Red" },
            ].map((b) => (
              <span
                key={b.label}
                className="rounded-full border px-3 py-1 text-xs font-medium"
                style={{ background: b.bg, borderColor: b.border, color: b.text }}
              >
                {b.label}
              </span>
            ))}
          </div>
        </Section>

        {/* Voz */}
        <Section title="Tom de Voz">
          <div className="grid gap-6 sm:grid-cols-2">
            <div className="rounded-2xl border border-teal-500/20 bg-teal-500/[0.05] p-6">
              <h3 className="mb-4 text-sm font-semibold text-teal-300">Fazer</h3>
              <ul className="space-y-2">
                {brand.voice.dos.map((d) => (
                  <li key={d} className="flex gap-2 text-sm text-slate-300">
                    <span className="mt-0.5 text-teal-400">✓</span>
                    {d}
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-red-500/20 bg-red-500/[0.04] p-6">
              <h3 className="mb-4 text-sm font-semibold text-red-300">Evitar</h3>
              <ul className="space-y-2">
                {brand.voice.donts.map((d) => (
                  <li key={d} className="flex gap-2 text-sm text-slate-300">
                    <span className="mt-0.5 text-red-400">✗</span>
                    {d}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Section>

        {/* Animações */}
        <Section title="Motion — Durações e Easing">
          <div className="grid gap-3 sm:grid-cols-3">
            {(Object.entries(brand.motion.duration) as [string, string][]).map(([name, val]) => (
              <div
                key={name}
                className="flex items-center justify-between rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-3"
              >
                <span className="text-sm text-white">{name}</span>
                <code className="font-mono text-xs text-teal-300">{val}</code>
              </div>
            ))}
            {(Object.entries(brand.motion.easing) as [string, string][]).map(([name, val]) => (
              <div
                key={name}
                className="flex items-center justify-between rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-3"
              >
                <span className="text-sm text-white">ease-{name}</span>
                <code className="font-mono text-[10px] text-teal-300 max-w-[140px] truncate" title={val}>{val}</code>
              </div>
            ))}
          </div>
        </Section>

        {/* Rodapé */}
        <div className="border-t border-white/[0.06] pt-8 text-center text-xs text-slate-600">
          <p>NeuroMent Brand Kit · Edite <code className="font-mono text-slate-500">src/design/brand.ts</code> para atualizar todos os tokens</p>
        </div>
      </div>
    </div>
  );
}
